import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { billing, subscriptions } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge } from '@repletoryreach/sdk-ui';
import { PageHeader, Alert, MetricCard, EmptyState } from '@repletoryreach/sdk-ui/data-display';
import { Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { openFileViewer } from '@repletoryreach/sdk-ui/file-viewer';
import { invoiceViewerSource } from './invoice-source';
import { ReferredByCard } from '../refer/components/ReferredByCard';
import { notifyInfo, notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useMutation } from '@tanstack/react-query';
import { isApiError } from '@repletoryreach/sdk-api';
import { formatMinor, formatDateTime, formatRelative, formatCount } from '../../lib/format';
import { useCredits } from '../../lib/credits';
import { useT } from '../../i18n/I18nProvider';
import { walletState, shownBalance } from '../../lib/wallet';
import { categoryLabel } from './components/usage-labels';
import './billing.css';
import { useWallet } from '../../data/useWallet';
import { InfoIcon } from '../../components/panel';
import { useMetaBilling } from '../../lib/meta-billing';
import { PlanCard } from './components/PlanCard';
import { PaymentCard, useAddCard } from './components/PaymentCard';
import { ExtraUseCard } from './components/ExtraUseCard';
import { TopupResultModal } from './components/TopupResultModal';
import { HelpButton } from '../../app/guides/HelpButton';

/** A payment id as billing writes it into the return address. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export default function BillingPage() {
  const t = useT();
  const credits = useCredits();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const addCard = useAddCard();

  /*
   * `billing-topup.md` §4 item 2: `return_url` pointed here and this page had
   * no notion of "you just came back from a gateway". With `staleTime: 30_000`
   * on a SHARED `['wallet']` key and nothing invalidating it, the balance a
   * returning tenant saw was very likely the pre-payment one they were looking
   * at sixty seconds earlier, from which they would reasonably conclude the
   * payment had failed.
   */
  const returned = params.get('topup') === 'returned';
  // Billing names the payment in the return address (21 September 2026), so the
  // page can say how it went instead of "if it went through".
  const returnedTopupId = params.get('topup_id');
  const [resultTopupId, setResultTopupId] = useState<string | null>(null);
  useEffect(() => {
    if (!returned) return;
    qc.invalidateQueries({ queryKey: ['wallet'] });
    qc.invalidateQueries({ queryKey: ['ledger'] });
    if (returnedTopupId && UUID.test(returnedTopupId)) setResultTopupId(returnedTopupId);
    else notifyInfo(t('billing.topup.returned'), t('nav.billing'));
    // Scrub the flags so a refresh does not re-announce it. Paystack adds its
    // own two to the address it returns to.
    setParams((prev) => {
      const q = new URLSearchParams(prev);
      for (const k of ['topup', 'topup_id', 'trxref', 'reference']) q.delete(k);
      return q;
    }, { replace: true });
  }, [returned]); // eslint-disable-line react-hooks/exhaustive-deps

  const walletQ = useWallet();
  const ledgerQ = useQuery({
    // §4 item 14: the two ledger queries used different keys, so nothing on
    // this page invalidated anything after a top-up. Both pages share these.
    queryKey: ['ledger', 'recent'],
    queryFn: () => billing.listLedger({ limit: 6 })
  });
  const subQ = useQuery({
    // §4 item 3: `past_due` is a status the SDK deliberately returns and no
    // tenant page called `getMine()`, so a tenant whose subscription had
    // lapsed saw a perfectly normal billing page.
    queryKey: ['subscription', 'mine'],
    queryFn: () => subscriptions.getMine(),
    retry: false
  });
  const invoicesQ = useQuery({
    // §4 item 6: `listInvoices` and `invoicePdfUrl` existed and were unused, so
    // a paying tenant could not get a receipt out of the console.
    queryKey: ['invoices', 'recent'],
    queryFn: () => billing.listInvoices({ limit: 5 }),
    retry: false
  });
  // Month-to-date, from the attributed ledger: the pace, the runway and the
  // split by category, so the balance reads as "how long" not just "how much".
  // A number on the business's own WhatsApp account (5 October 2026): its messages are billed by Meta, not in credits.
  const ownAccount = useMetaBilling().hasOwnAccount;
  const usageQ = useQuery({ queryKey: ['usage', 'summary', 'month'], queryFn: () => billing.getUsageSummary(), retry: false });
  const usage = usageQ.data?.data;

  const wallet = walletQ.data?.data;
  const ledger = ledgerQ.data?.data ?? [];
  const subscription = subQ.data?.data ?? null;
  const invoices = invoicesQ.data?.data ?? [];

  // Last month's usage statement, built on demand if the monthly pass has not run yet.
  const previousMonth = useMemo(() => {
    const d = new Date();
    const p = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
    return `${p.getUTCFullYear()}-${String(p.getUTCMonth() + 1).padStart(2, '0')}`;
  }, []);
  const statement = useMutation({
    mutationFn: () => billing.generateStatement(previousMonth),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices'] });
      notifySuccess(res.meta.created ? t('billing.statements.created', { number: res.data.invoice_number }) : t('billing.statements.exists', { number: res.data.invoice_number }), t('billing.invoices'));
    },
    onError: (e) => notifyError(isApiError(e) && e.code === 'NO_USAGE' ? t('billing.statements.noUsage', { period: previousMonth }) : (e instanceof Error ? e.message : t('common.unknownError')), t('billing.invoices'))
  });

  const currency = wallet?.currency ?? 'ZAR';
  // Extra use (30 October 2026): while it is active the wallet may go below zero, which is what is owed this
  // month. The page shows extra use instead of a negative balance.
  const extra = wallet?.extra_use ?? null;
  const balance = shownBalance(wallet?.credits_minor ?? 0, extra);
  // The two parts of a balance (21 September 2026). What a plan put in is
  // spent first and only on messages; a plan's fee comes out of what was
  // bought. With nothing from a plan there is one part, and the card says
  // nothing more than it always did.
  const included = wallet?.included_minor ?? 0;
  const purchased = wallet?.purchased_minor ?? Math.max(0, balance - included);
  // The state is the server's: the same line the platform alerts on.
  const state = wallet ? walletState(wallet.credits_minor, currency, wallet.state, extra) : 'ok';
  const empty = state === 'empty';
  const low = state === 'low';
  const extraLine = extra && (extra.active || extra.used_minor > 0)
    ? t('billing.extra.thisMonth', { used: formatMinor(extra.used_minor, currency), limit: formatMinor(extra.limit_minor, currency) })
    : null;

  /*
   * §4 item 8: the tenant saw a balance with no way to translate it into
   * messages. Derived from what they have actually been charged, so it follows
   * their own template mix rather than a list price we would have to guess at.
   */
  const perMessage = useMemo(() => {
    const debits = ledger.filter((e) => e.amount_minor < 0 && e.sku_code);
    if (debits.length < 3) return null;
    const total = debits.reduce((sum, e) => sum + Math.abs(e.amount_minor), 0);
    const avg = total / debits.length;
    return avg > 0 ? { avg, remaining: Math.floor(balance / avg) } : null;
  }, [ledger, balance]);

  return (
    <Stack gap="lg">
      <PageHeader title={t('nav.billing')} description={t('billing.description')} withDivider={false} actions={<HelpButton guide="account" />} />

      {walletQ.error && <Alert color="error" role="alert">{t('billing.loadError')}</Alert>}

      {/*
        W47. Only while it is still true: the card asks who referred this
        account for a short while after signing up and before the first
        payment, and says who it was once the answer is in.
      */}
      <ReferredByCard />

      {/*
        §4 item 1, "the single highest-value fix on the page": at zero the old
        code showed the same "running low" warning as a tenant with R40 left.
        At zero, sending is already BLOCKED, not running low, and
        INSUFFICIENT_CREDITS (the send-time code an agent sees in the inbox) was
        explained nowhere in the tenant console.
      */}
      {empty && (
        <Alert color="error" role="alert" title={t('billing.empty.title')}>
          <Stack gap={8}>
            <Text role="bodySmall">{t('billing.empty.body')}</Text>
            <div><Button size="xs" onClick={() => nav('/billing/topup')}>{t('billing.topUpNow')}</Button></div>
          </Stack>
        </Alert>
      )}

      {/* The credit has run out but extra use carries on: calm, not an error, and what it has reached so far. */}
      {state === 'extra' && extra && (
        <Alert color="info" title={t('billing.extra.alert.title')} data-testid="billing-extra-alert">
          <Stack gap={8}>
            <Text role="bodySmall">{t('billing.extra.alert.body', { used: formatMinor(extra.used_minor, currency), limit: formatMinor(extra.limit_minor, currency) })}</Text>
            <div><Button size="xs" variant="default" onClick={() => nav('/billing/topup')}>{t('billing.topUpNow')}</Button></div>
          </Stack>
        </Alert>
      )}

      {low && (
        <Alert color="warning" title={t('billing.lowBalance')}>
          {perMessage
            ? t('billing.lowBalance.bodyWithCount', { count: formatCount(perMessage.remaining) })
            : t('billing.lowBalance.body')}{' '}
          <Link to="/billing/topup">{t('billing.topUpNow')}</Link>
          {' · '}
          <Link to="/billing/alerts">{wallet?.auto_topup_enabled ? t('billing.autoTopup.on') : t('billing.autoTopup.setUp')}</Link>
          {extra?.active && <>{' '}{t('billing.extra.lowNote')}</>}
        </Alert>
      )}

      {subscription && subscription.status === 'past_due' && (wallet?.pays_by_invoice ? (
        // Pays by invoice (18 October 2026): the plan waits for their payment, which RepletoryReach adds as credit.
        <Alert color="warning" role="alert" title={t('billing.pastDue.title')} data-testid="billing-past-due-invoice">
          <Text role="bodySmall">{t('billing.pastDue.invoiceBody')}</Text>
        </Alert>
      ) : (
        <Alert color="warning" role="alert" title={t('billing.pastDue.title')}>
          <Stack gap={8}>
            <Text role="bodySmall">{t('billing.pastDue.body')}</Text>
            {/* Plans are paid by card (11 October 2026): a new card is charged straight away. */}
            <div><Button size="xs" onClick={() => addCard.mutate()} loading={addCard.isPending}>{t('billing.pastDue.action')}</Button></div>
          </Stack>
        </Alert>
      ))}

      {/* §4 items 10 and 11: a bare `cols={3}` squeezed three cards into three
          narrow columns on a phone, and the hand-rolled cards forfeited the
          loading skeleton that `MetricCard` gives for free. */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md" data-guide="billing-balance">
        <div className="rr-billing__metric">
          <MetricCard
            label={t('billing.walletBalance')}
            value={wallet ? credits(balance, currency) : t('dashboard.metrics.unavailable')}
            hint={perMessage ? t('billing.roughlyMessages', { count: formatCount(perMessage.remaining) }) : undefined}
            loading={walletQ.isPending}
          />
          {/* Credit set aside for a phone call under way, or a fee awaiting approval:
              not spent, and it explains why a message may be refused on a wallet
              that does not look empty. */}
          {extraLine && (
            <div className="rr-billing__split" data-testid="wallet-extra">
              <Text role="bodySmall" span>{extraLine}</Text>
            </div>
          )}
          {(wallet?.held_minor ?? 0) > 0 && (
            <div className="rr-billing__split" data-testid="wallet-held">
              <Tooltip label={t('billing.wallet.heldHint')} position="top" maxWidth={280}>
                <Text role="bodySmall" span>{t('billing.wallet.held', { amount: credits(wallet!.held_minor!, currency) })}</Text>
              </Tooltip>
            </div>
          )}
          {included > 0 && (
            <div className="rr-billing__split">
              <Text role="bodySmall" span>{t('billing.wallet.bought', { amount: credits(purchased, currency) })}</Text>
              <span className="rr-billing__split-row">
                <Text role="bodySmall" span>{t('billing.wallet.fromPlan', { amount: credits(included, currency) })}</Text>
                {/* A button, not a bare icon: a tooltip opens on focus as well
                    as hover, so a keyboard and a touch screen both reach it. */}
                <Tooltip label={t('billing.wallet.split.info')} position="top" maxWidth={280}>
                  <button type="button" className="rr-billing__info" aria-label={t('billing.wallet.split.infoLabel')}>
                    <InfoIcon />
                  </button>
                </Tooltip>
              </span>
            </div>
          )}
          <Link to="/billing/topup" className="rr-billing__link">{t('billing.topUp')}</Link>
        </div>

        <MetricCard
          label={t('billing.creditValue')}
          value={t('billing.creditValue.value')}
          hint={perMessage ? t('billing.perMessage', { amount: credits(Math.round(perMessage.avg), currency) }) : t('billing.creditValue.hint')}
          loading={walletQ.isPending}
        />

        <MetricCard
          label={t('common.updated')}
          // §4 item 2: permanently "–" because `updated_at` was on the row and
          // off the wire. It is projected now.
          value={wallet?.updated_at ? formatRelative(wallet.updated_at) : t('common.none')}
          hint={wallet?.updated_at ? formatDateTime(wallet.updated_at) : undefined}
          loading={walletQ.isPending}
        />
      </SimpleGrid>

      <div data-guide="billing-plan"><PlanCard subscription={subscription} loading={subQ.isPending} byInvoice={Boolean(wallet?.pays_by_invoice)} extraActive={Boolean(extra?.active)} /></div>
      {/* A business that pays by invoice has no card for its plan (18 October 2026). */}
      {!wallet?.pays_by_invoice && <PaymentCard paidPlan={Boolean(subscription && subscription.status !== 'cancelled')} focus={params.get('card') === 'update'} />}
      {/* Extra use on Growth and Scale (30 October 2026): keep going after the credit runs out, charged to the card. */}
      <ExtraUseCard byInvoice={Boolean(wallet?.pays_by_invoice)} />

      <Card withBorder data-guide="billing-month">
        <Stack gap="sm">
          <Group justify="space-between" align="flex-start" wrap="wrap">
            <Stack gap={2}>
              <Text role="body" fw={600}>{t('billing.usage.thisMonth')}</Text>
              <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.usage.thisMonthHint')}</Text>
            </Stack>
            <Group gap="md">
              <Link to="/billing/usage" className="rr-billing__link">{t('billing.usage.fullUsage')}</Link>
              <Link to="/billing/alerts" className="rr-billing__link">{t('billing.alerts.title')} →</Link>
            </Group>
          </Group>
          <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
            <MetricCard label={t('billing.usage.kpi.spent')} value={usage ? credits(usage.spent_minor, currency) : '–'} loading={usageQ.isPending} />
            <MetricCard label={t('billing.usage.kpi.perDay')} value={usage ? credits(usage.avg_daily_spend_minor, currency) : '–'} loading={usageQ.isPending} />
            <MetricCard
              label={t('billing.usage.kpi.runway')}
              value={usage ? (usage.runway_days == null ? t('billing.usage.kpi.runwayNone') : t('billing.usage.kpi.runwayDays', { count: formatCount(Number(usage.runway_days)) })) : '–'}
              loading={usageQ.isPending}
            />
            <MetricCard
              label={t('billing.usage.kpi.projectedLabel')}
              value={usage?.projected_month_end_minor != null ? credits(usage.projected_month_end_minor, currency) : '–'}
              loading={usageQ.isPending}
            />
          </SimpleGrid>
          {/* Messages on the business's own WhatsApp account are billed by Meta, so they never show as spend here. */}
          {ownAccount && (
            <Text role="caption" c="var(--rr-color-text-muted)" data-testid="own-account-spend-note">{t('billing.usage.ownAccountNote')}</Text>
          )}
          {usage && usage.by_category.length > 0 && (
            <Group gap="xs" wrap="wrap">
              {usage.by_category.map((c) => (
                <Badge key={c.category} size="sm" variant="light">{categoryLabel(t, c.category)}: {credits(c.amount_minor, currency)}</Badge>
              ))}
            </Group>
          )}
          {wallet && (
            <Text role="caption" c="var(--rr-color-text-muted)">
              {wallet.auto_topup_enabled
                ? t('billing.autoTopup.onHint', { amount: formatMinor(wallet.auto_topup_amount_minor ?? 0, currency), method: wallet.primary_payment_method ?? '' })
                : t('billing.autoTopup.offHint', { threshold: credits(wallet.low_balance_minor, currency) })}
            </Text>
          )}
        </Stack>
      </Card>

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md">
        <Card withBorder>
          <Stack gap="sm">
            <Group justify="space-between">
              <Text role="body" fw={600}>{t('billing.recentTransactions')}</Text>
              <Link to="/billing/ledger" className="rr-billing__link">{t('billing.fullLedger')}</Link>
            </Group>

            {ledgerQ.error && <Alert color="error" role="alert">{t('billing.ledgerError')}</Alert>}

            {!ledgerQ.isPending && !ledgerQ.error && ledger.length === 0 && (
              <EmptyState title={t('billing.noTransactionsYet')} description={t('billing.noTransactions.hint')} />
            )}

            {ledger.map((e) => (
              <Group key={e.id} justify="space-between" wrap="nowrap">
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Text role="bodySmall" truncate>{e.description ?? e.transaction_type_code}</Text>
                  <Text role="caption" c="var(--rr-color-text-muted)">{formatDateTime(e.created_at)}</Text>
                </Stack>
                {/* §4 item 13: colour was the only signal, and a debit rendered
                    with no explicit sign beyond the negative number itself. */}
                <Text
                  role="bodySmall"
                  fw={600}
                  className={`rr-billing__amount rr-billing__amount--${e.amount_minor < 0 ? 'debit' : 'credit'}`}
                >
                  {e.amount_minor < 0 ? '−' : '+'}
                  {/* §4 item 4: every row was formatted in the WALLET currency
                      even when the entry was in another one. */}
                  {credits(Math.abs(e.amount_minor), e.currency ?? currency)}
                  <span className="rr-visually-hidden">
                    {e.amount_minor < 0 ? t('billing.debit') : t('billing.credit')}
                  </span>
                </Text>
              </Group>
            ))}

            <div>
              <Button size="sm" onClick={() => nav('/billing/topup')}>{t('dashboard.quickActions.topUp')}</Button>
            </div>
          </Stack>
        </Card>

        <Card withBorder data-guide="billing-invoices">
          <Stack gap="sm">
            <Group justify="space-between" wrap="wrap">
              <Text role="body" fw={600}>{t('billing.invoices')}</Text>
              <Button size="xs" variant="default" loading={statement.isPending} onClick={() => statement.mutate()}>
                {t('billing.statements.generate', { period: previousMonth })}
              </Button>
            </Group>
            <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.statements.hint')}</Text>

            {!invoicesQ.isPending && invoices.length === 0 && (
              <EmptyState title={t('billing.invoices.empty')} description={t('billing.invoices.empty.hint')} />
            )}

            {invoices.map((inv) => (
              <Group key={inv.id} justify="space-between" wrap="nowrap">
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Group gap={6}>
                    <Text role="bodySmall" truncate>{inv.invoice_number}</Text>
                    <Badge size="xs" variant="light">{t(`billing.invoiceStatus.${inv.status}`, inv.status)}</Badge>
                    {inv.kind === 'usage_statement' && <Badge size="xs" variant="light">{t('billing.invoiceKind.usage_statement')}</Badge>}
                  </Group>
                  <Text role="caption" c="var(--rr-color-text-muted)">{inv.issued_at ? formatDateTime(inv.issued_at) : t('billing.invoices.notIssued')}</Text>
                </Stack>
                <Group gap="xs" wrap="nowrap">
                  <Text role="bodySmall" fw={600}>{formatMinor(inv.total_minor, inv.currency)}</Text>
                  <Tooltip label={t('billing.invoices.view')} position="left">
                    <Button size="xs" variant="light" onClick={() => openFileViewer(invoiceViewerSource(inv, t))}>
                      {t('action.view')}
                    </Button>
                  </Tooltip>
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {resultTopupId && (
        <TopupResultModal
          topupId={resultTopupId}
          onClose={() => setResultTopupId(null)}
          onUnknown={() => {
            setResultTopupId(null);
            notifyInfo(t('billing.topup.returned'), t('nav.billing'));
          }}
        />
      )}
    </Stack>
  );
}
